import { useCallback, useMemo, type MouseEvent } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { isPast } from 'utility/datetime';
import {
  getAssigneeDisplayName,
  formatAssociationsAsHtml,
  formatActivityDateTime,
} from '../helpers';
import type { ActivityPillProps } from '../types';
import { Tooltip, useTooltip } from '@kizen/kds/Tooltip';
import EventPill from '__components/Kizen/EventPill';
import { markdownToHtmlSync } from '@kizen/parse-md';
import { usePillHover } from '../HoverContext';
import css from '@emotion/css';
import { getTooltipPosition } from './common';

const truncateString = (str: string, maxLength: number): string => {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength) + '...';
};

const StyledEventPill = styled(EventPill)<{
  customColor?: string;
  canEdit?: boolean;
  maxWidth?: string;
  isHovered?: boolean;
}>`
  margin-bottom: 5px;
  min-height: 21px;
  ${({ maxWidth }) => maxWidth && `max-width: ${maxWidth};`}
  cursor: ${({ canEdit }) => (canEdit ? 'pointer' : 'default')};

  /* Override the left bar color with custom color */
  ${({ customColor }) =>
    customColor &&
    `
    & > div:first-of-type {
      background-color: ${customColor} !important;
    }
  `}

  & p {
    color: var(--color-font-primary) !important;
  }

  ${({ isHovered }) =>
    isHovered
      ? css`
          background-color: var(--color-background-light);

          & .menu-button-wrapper {
            opacity: 1;
          }
        `
      : ''}
`;

export const ActivityPill = ({
  activity,
  timezone,
  businessTimezone,
  onComplete,
  onEdit,
  onDelete,
  canEdit,
  columnIndex,
  totalColumns,
  maxWidth,
  tooltipPosition: tooltipPositionProps,
  isMobile,
  disableHoverEffect = false,
}: ActivityPillProps) => {
  const { t } = useTranslation();

  const { onPillHover, isHovered } = usePillHover();

  const activityDate = new Date(activity.dueDatetime);
  const isActivityPast = isPast(activityDate);
  const pillColor = isActivityPast
    ? 'var(--color-viz-cat-7-primary)'
    : 'var(--color-viz-cat-2-primary)';
  const activityType = activity.activityObject.name;

  const handlePillClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      onComplete(activity);
    },
    [activity, onComplete]
  );

  const handleComplete = useCallback(() => {
    onComplete(activity);
  }, [activity, onComplete]);

  const handleEdit = useCallback(() => {
    onEdit(activity);
  }, [activity, onEdit]);

  const handleDelete = useCallback(() => {
    onDelete(activity.id);
  }, [activity.id, onDelete]);

  const hasAssociations =
    activity.associatedEntities && activity.associatedEntities.length > 0;

  // Build markdown content (not HTML) for proper parsing
  const nameMaxLength = isMobile ? 50 : 100;
  const tooltipMarkdown = `**${truncateString(activity.activityObject.name, nameMaxLength)}**\n\n${t('Assignee')}: ${getAssigneeDisplayName(activity)}\n\n${t('Due')}: ${formatActivityDateTime(activity.dueDatetime, timezone, businessTimezone)}${
    hasAssociations
      ? `\n\n${t('Associations')}: ${formatAssociationsAsHtml(activity)}`
      : ''
  }`;

  const parsedMarkDown = useMemo(() => {
    return markdownToHtmlSync(tooltipMarkdown, { forceExternalLinks: true });
  }, [tooltipMarkdown]);

  const menuOptions = [
    {
      value: 'complete',
      label: t('Complete'),
    },
    ...(canEdit
      ? [
          {
            value: 'edit',
            label: t('Edit'),
          },
          {
            value: 'delete',
            label: t('Delete'),
          },
        ]
      : []),
  ] as { value: string; label: string }[];

  const handleMenuSelect = useCallback(
    (option: { value: string }) => {
      if (option.value === 'complete') {
        handleComplete();
      } else if (option.value === 'edit') {
        handleEdit();
      } else if (option.value === 'delete') {
        handleDelete();
      }
    },
    [handleComplete, handleEdit, handleDelete]
  );

  const handleHoverChange = useCallback(
    (hovered: boolean) => {
      if (disableHoverEffect) {
        onPillHover?.(activity.id, hovered);
      }
    },
    [activity.id, onPillHover, disableHoverEffect]
  );

  const { triggerProps, targetProps, showTooltip, tooltipProps } = useTooltip(
    true,
    handleHoverChange
  );

  const tooltipPosition = getTooltipPosition(
    tooltipPositionProps,
    columnIndex,
    totalColumns
  );

  return (
    <span {...targetProps} {...triggerProps}>
      {showTooltip && (
        <Tooltip
          position={tooltipPosition as any}
          interactive
          html={parsedMarkDown}
          {...tooltipProps}
        />
      )}
      <StyledEventPill
        date={activityDate}
        type="activity"
        name={activityType}
        maxWidth={maxWidth}
        onClick={handlePillClick}
        data-qa-id={`activity-pill-${activity.id}`}
        // Used to style the hover effect for multi-day activities that span across multiple cells
        isHovered={disableHoverEffect && isHovered(activity.id)}
        customColor={pillColor}
        canEdit={canEdit}
        isInvalid={false}
        isPastDate={false}
        showActionMenu={canEdit}
        actionMenuOptions={menuOptions}
        onActionMenuSelect={handleMenuSelect}
        actionMenuDataQaId={`activity-pill-menu-${activity.id}`}
        enableHoverEffect={canEdit && !disableHoverEffect}
        disableTooltip={true}
        isSpanning={activity.isSpanning}
      />
    </span>
  );
};
